// pages/index.tsx
import Head from "next/head";
import { GetStaticProps } from "next";
import HomepageLanding, {
  HomepageCityCard,
  HomepageFaq,
  HomepageVenueCard,
} from "../src/components/HomepageLanding";
import { getAllCities, getCitiesWithBooking } from "../src/config/cityData";
import { fetchGraphQL, fetchCityCatalog } from "../src/utils/cityApi";
import { getCanonicalUrl } from "../src/utils/seoHelpers";
import { GET_ALL_TURFS } from "../src/utils/queries";
import { buildTurfDetailRoute } from "../src/utils/turfRoutes";
import { transformTurfData } from "../src/utils/transformTurfData";
import { getVisiblePitches, hasVisiblePitches } from "../src/utils/turfVisibility";

type HomepageStat = {
  value: string;
  label: string;
  detail: string;
};

type CityEntry = ReturnType<typeof getAllCities>[number];

interface HomepageProps {
  cityDirectory: HomepageCityCard[];
  featuredCities: HomepageCityCard[];
  featuredVenues: HomepageVenueCard[];
  stats: HomepageStat[];
}

interface TurfQueryResponse {
  turfs?: {
    turfs?: any[];
    totalCount?: number;
  };
}

const homepageFaqs: HomepageFaq[] = [
  {
    question: "How can I book a turf on TurfBooking.in?",
    answer:
      "Choose your city, explore verified turfs and courts, compare photos, pricing, timings and amenities, then open the venue that fits your game and continue to its booking flow.",
  },
  {
    question: "Does TurfBooking charge extra over the venue price?",
    answer:
      "TurfBooking focuses on exact venue pricing so users can compare and book without hidden charges or extra commission added on top of the listed venue price.",
  },
  {
    question: "What details can I check before choosing a venue?",
    answer:
      "You can check venue photos, starting price, area or pitch size, location, contact details, amenities and special tags like partnered, football-friendly or rain-protected where available.",
  },
  {
    question: "Can I browse turfs by city and area?",
    answer:
      "Yes. TurfBooking supports city-level browsing and area-specific discovery so you can narrow your options to the locality that is most convenient for your team.",
  },
  {
    question: "Does TurfBooking also have a sports community?",
    answer:
      "Yes. Along with venue discovery for cricket, football, pickleball and badminton, TurfBooking is building a sports community where users can join the WhatsApp group for updates, connections and local sports activity.",
  },
];

const homepageTitle =
  "Book Sports Venues at Exact Venue Price | TurfBooking.in";
const homepageDescription =
  "Book cricket turfs, football grounds, pickleball and badminton venues across cities on TurfBooking.in. Compare verified venues, photos, timings and prices at exact venue price with no hidden charges or commission, and join our sports community.";

const getStartingPrice = (rawTurf: any): number | null => {
  const pitchPrices = Array.isArray(rawTurf?.Pitch)
    ? getVisiblePitches<any>(rawTurf.Pitch).map((pitch: any) => Number(pitch?.basePrice)).filter(
        (price: number) => Number.isFinite(price) && price > 0
      )
    : [];

  if (!pitchPrices.length) return null;
  return Math.min(...pitchPrices);
};

const getFeaturedSpotlightCities = (cities: CityEntry[], limit: number): CityEntry[] => {
  const selected: CityEntry[] = [];
  const usedSlugs = new Set<string>();

  const addCity = (city?: CityEntry) => {
    if (!city || usedSlugs.has(city.slug) || selected.length >= limit) return;

    selected.push(city);
    usedSlugs.add(city.slug);
  };

  addCity(cities.find((city) => city.slug === "indore"));
  addCity(cities.find((city) => city.slug === "mumbai"));
  addCity(cities.find((city) => city.slug === "kolkata"));
  addCity(cities.find((city) => city.slug === "delhi"));

  cities.forEach((city) => {
    if (selected.length >= limit) return;
    addCity(city);
  });

  return selected;
};

export const getStaticProps: GetStaticProps<HomepageProps> = async () => {
  const hardcodedCities = getAllCities()
    .sort((left, right) => {
      if (left.tier !== right.tier) return left.tier - right.tier;
      return left.name.localeCompare(right.name);
    });

  const bookingCities = getCitiesWithBooking()
    .sort((left, right) => {
      if (left.tier !== right.tier) return left.tier - right.tier;
      return left.name.localeCompare(right.name);
    });

  const spotlightCities = getFeaturedSpotlightCities(bookingCities, 6);

  // Only include cities that actually have turfs (avoid linking to 404 pages)
  let activeCitySlugs: Set<string> | null = null;
  try {
    const cityCatalog = await fetchCityCatalog();
    activeCitySlugs = new Set(cityCatalog.map((c) => c.slug));
  } catch {
    // Fallback: show all hardcoded cities if API fails
    activeCitySlugs = null;
  }

  const cityDirectorySource = activeCitySlugs
    ? hardcodedCities.filter((city) => activeCitySlugs!.has(city.slug))
    : hardcodedCities;

  const cityDirectory: HomepageCityCard[] = cityDirectorySource.map((city) => ({
    slug: city.slug,
    name: city.name,
    state: city.state,
    turfCount: null,
    priceFrom: null,
    imageUrl: null,
    highlightVenue: null,
  }));

  let featuredCities = cityDirectory.slice(0, 6);
  let featuredVenues: HomepageVenueCard[] = [];

  try {
    const spotlightResponses = await Promise.all(
      spotlightCities.map(async (city) => {
        const result = await fetchGraphQL<TurfQueryResponse>(GET_ALL_TURFS, {
          city: city.name,
          verificationStatus: "VERIFIED",
        });

        const visibleTurfs = (result?.turfs?.turfs || []).filter(
          (turf: any) => turf?.hideTurf !== true && hasVisiblePitches(turf)
        );

        return {
          city,
          totalCount: visibleTurfs.length,
          turfs: visibleTurfs.slice(0, 3),
        };
      })
    );

    featuredCities = spotlightResponses.map(({ city, totalCount, turfs }) => {
      const leadTurf = turfs[0] || null;
      const transformedLead = leadTurf ? transformTurfData(leadTurf, city.name) : null;

      return {
        slug: city.slug,
        name: city.name,
        state: city.state,
        turfCount: totalCount,
        priceFrom:
          transformedLead?.data?.areaPrice?.[0]?.price?.weekday?.regularHours ||
          getStartingPrice(leadTurf),
        imageUrl:
          transformedLead?.imageUrl?.[0] ||
          leadTurf?.coverImage ||
          leadTurf?.images?.[0] ||
          null,
        highlightVenue: transformedLead?.name || null,
      };
    });

    featuredVenues = spotlightResponses
      .flatMap(({ city, turfs }) =>
        turfs
          .map((rawTurf) => {
            const transformed = transformTurfData(rawTurf, city.name);
            const firstPitch = transformed?.data?.areaPrice?.[0];

            if (!transformed?.userName || !firstPitch?.pitchName) {
              return null;
            }

            return {
              id: `${city.slug}-${transformed.userName}`,
              name: transformed.name,
              cityName: city.name,
              citySlug: city.slug,
              areaName: transformed.data.areaName || null,
              address: transformed.data.address || null,
              imageUrl:
                transformed.imageUrl?.[0] ||
                rawTurf?.coverImage ||
                rawTurf?.images?.[0] ||
                "https://s3.ap-south-1.amazonaws.com/turfbooking.in-bucket/placeholder-turf.jpg",
              priceFrom:
                firstPitch.price?.weekday?.regularHours || getStartingPrice(rawTurf),
              href: buildTurfDetailRoute({
                city: city.slug,
                userName: transformed.userName,
                pitchName: firstPitch.pitchName,
                areaName: transformed.data.areaName || undefined,
              }),
              isPartner: Boolean(transformed.data.partnership),
              supportedSports: transformed.supportedSports || [],
              rainProtected: Boolean(transformed.data.isRainProtected),
              hasCafe: Boolean(transformed.data.hasCafe),
            } satisfies HomepageVenueCard;
          })
          .filter(Boolean) as HomepageVenueCard[]
      )
      .slice(0, 6);

  } catch {
    featuredCities = cityDirectory.slice(0, 6);
    featuredVenues = [];
  }

  const stats: HomepageStat[] = [
    {
      value: `${cityDirectorySource.length}+`,
      label: "booking cities",
      detail: "city pages available for live venue discovery",
    },
    {
      value: "280+",
      label: "verified listings",
      detail: "venues surfaced from active city pages",
    },
    {
      value: "0",
      label: "hidden charges",
      detail: "exact venue pricing with no extra commission added on top",
    },
    {
      value: "4",
      label: "sports formats",
      detail: "cricket, football, pickleball and badminton",
    },
  ];

  return {
    props: {
      cityDirectory,
      featuredCities,
      featuredVenues,
      stats,
    },
    revalidate: 600,
  };
};

export default function IndexPage({
  cityDirectory,
  featuredCities,
  featuredVenues,
  stats,
}: HomepageProps) {
  const canonicalUrl = getCanonicalUrl("/");
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: "TurfBooking.in",
        url: canonicalUrl,
        description: homepageDescription,
      },
      {
        "@type": "FAQPage",
        mainEntity: homepageFaqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <Head>
        <title>{homepageTitle}</title>
        <meta name="description" content={homepageDescription} />
        <meta
          name="keywords"
          content="turf booking, book sports venues, cricket turf booking, football ground booking, pickleball courts, badminton venue booking, sports venues near me, exact venue price, no hidden charges, no commission booking, sports community"
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content={homepageTitle} />
        <meta property="og:description" content={homepageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://turfbooking.in/TBlogo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={homepageTitle} />
        <meta name="twitter:description" content={homepageDescription} />
        <meta name="twitter:image" content="https://turfbooking.in/TBlogo.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      </Head>
      <HomepageLanding
        cityDirectory={cityDirectory}
        featuredCities={featuredCities}
        featuredVenues={featuredVenues}
        stats={stats}
        faqs={homepageFaqs}
      />
    </>
  );
}
